// LoginScreen.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import apiClient from "../utils/apiClient"; // Adjust the import path as needed
import { POLICY_CHECK_KEY } from "../utils/session";
import { markLogin } from "../utils/sessionOnly";
import ButtonPrimary from "../components/ButtonPrimary"; // Adjust the import path as needed
import logo from "../resources/cadmus_dark.svg";
import "./LoginScreen.css";

// E-mailadres dat bij "Remember me" bewaard wordt (staat niet in clearSession,
// dus blijft na uitloggen staan).
const REMEMBER_EMAIL_KEY = "login_remember_email";

const readRememberedEmail = () => {
  try { return localStorage.getItem(REMEMBER_EMAIL_KEY) || ""; } catch { return ""; }
};

// Bewust geen autocomplete-hints (username/current-password/one-time-code):
// die laten macOS/Chrome steeds de melding "Enable Password AutoFill" tonen.

const inputStyle = {
  color: "#111",
  fontFamily: "DM Sans",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "16px",
  opacity: 0.4,
};

// Wachtwoordveld met oogje om het wachtwoord te tonen
function PasswordInput({ id, value, onChange, onKeyDown, placeholder, autoComplete, blockAutofill }) {
  const [visible, setVisible] = useState(false);
  // Na een reset het veld readOnly tot je erin klikt: de browser vult een
  // readOnly-veld niet automatisch met een (oud) bewaard wachtwoord.
  const [locked, setLocked] = useState(!!blockAutofill);
  const lockProps = locked
    ? { readOnly: true, onFocus: () => setLocked(false), onPointerDown: () => setLocked(false) }
    : {};
  return (
    <div className="login-password-wrap">
      <input
        type={visible ? "text" : "password"}
        id={id}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        onKeyDown={onKeyDown}
        required
        style={inputStyle}
        {...lockProps}
      />
      <button
        type="button"
        className="login-eye"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? "Hide password" : "Show password"}
        title={visible ? "Hide password" : "Show password"}
      >
        {visible ? <VisibilityOutlinedIcon fontSize="small" /> : <VisibilityOffOutlinedIcon fontSize="small" />}
      </button>
    </div>
  );
}

// 6 losse vakjes voor de reset-code. Plakken (of autofill vanuit de mail)
// in een willekeurig vakje vult ze allemaal; typen springt door, backspace
// springt terug.
const CODE_LEN = 6;
// status: "idle" | "checking" | "ok" | "error";
// shake verandert bij elke foute code, zodat de schud-animatie opnieuw speelt.
function CodeBoxes({ value, onChange, status = "idle", shake = 0 }) {
  const locked = status === "ok" || status === "checking";
  const refs = React.useRef([]);
  const focus = (i) => {
    const el = refs.current[Math.max(0, Math.min(CODE_LEN - 1, i))];
    if (el) { el.focus(); el.select(); }
  };
  // Cijfers vanaf positie i invullen (1 bij typen, meer bij plakken/autofill)
  const fill = (i, text) => {
    const digits = String(text).replace(/\D/g, "");
    if (!digits) return;
    const start = digits.length >= CODE_LEN ? 0 : i;
    const arr = value.padEnd(CODE_LEN, " ").split("");
    digits.slice(0, CODE_LEN - start).split("").forEach((d, k) => { arr[start + k] = d; });
    onChange(arr.join("").replace(/\s+$/, ""));
    focus(Math.min(start + digits.length, CODE_LEN - 1));
  };
  const clear = (i) => {
    const arr = value.padEnd(CODE_LEN, " ").split("");
    arr[i] = " ";
    onChange(arr.join("").replace(/\s+$/, ""));
  };
  return (
    <div
      key={shake}
      className={`login-code-boxes${status !== "idle" ? ` is-${status}` : ""}${shake ? " is-shake" : ""}`}
    >
      {Array.from({ length: CODE_LEN }, (_, i) => {
        const ch = value[i] && value[i] !== " " ? value[i] : "";
        return (
          <input
            key={i}
            ref={(el) => { refs.current[i] = el; }}
            type="text"
            inputMode="numeric"
            aria-label={`Digit ${i + 1}`}
            autoFocus={i === 0}
            readOnly={locked}
            value={ch}
            onFocus={(e) => e.target.select()}
            onChange={(e) => {
              // Browser-autofill zet soms het e-mailadres in een vakje
              // (bv. "…sys4096.nl" -> "4" in het laatste vakje): negeren.
              if (/[^\d\s-]/.test(e.target.value) && e.target.value.length > 1) return;
              const v = e.target.value.replace(/\D/g, "");
              if (!v) return clear(i);
              // Bij typen over een bestaand cijfer: alleen het nieuwe cijfer
              fill(i, v.length === 2 && ch ? v.replace(ch, "") || v[1] : v);
            }}
            onPaste={(e) => {
              e.preventDefault();
              fill(i, e.clipboardData.getData("text"));
            }}
            onKeyDown={(e) => {
              if (e.key === "Backspace" && !ch && i > 0) {
                e.preventDefault();
                clear(i - 1);
                focus(i - 1);
              } else if (e.key === "ArrowLeft") {
                e.preventDefault();
                focus(i - 1);
              } else if (e.key === "ArrowRight") {
                e.preventDefault();
                focus(i + 1);
              }
            }}
          />
        );
      })}
    </div>
  );
}

// Melding onder de code-vakjes
const CodeStatus = ({ status, message }) =>
  status === "checking" ? <div className="login-code-msg">Checking code…</div>
  : status === "ok" ? <div className="login-code-msg is-ok">✓ Code verified. Choose your new password.</div>
  : status === "error" ? <div className="login-code-msg is-error">{message}</div>
  : null;

// Velden in één kader met scheidingslijn
const Fields = ({ children, className = "" }) => (
  <div className={`login-fields ${className}`.trim()}>{children}</div>
);

function LoginScreen() {
  const navigate = useNavigate();
  const [username, setUsername] = useState(readRememberedEmail);
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(() => !!readRememberedEmail());
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [leaving, setLeaving] = useState(false);
  const [busy, setBusy] = useState(false);
  // "login" | "forgot" (e-mail invullen) | "reset" (code + nieuw wachtwoord)
  const [step, setStep] = useState("login");
  // true nadat het wachtwoord net is gereset: wachtwoordveld leeg houden
  const [justReset, setJustReset] = useState(false);
  // Inloggen mislukt: velden rood + schudden; telt op
  // zodat de schud-animatie bij elke nieuwe poging opnieuw speelt.
  const [loginFailed, setLoginFailed] = useState(0);
  // Schudden via de Web Animations API i.p.v. de velden opnieuw te mounten:
  // een remount laat de browser de velden opnieuw autofillen, wat de melding
  // meteen weer wegveegde.
  useEffect(() => {
    if (!loginFailed) return;
    document.getElementById("password")?.focus();
    document.querySelector(".login-fields")?.animate?.(
      [0, -8, 7, -5, 3, 0].map((x) => ({ transform: `translateX(${x}px)` })),
      { duration: 400, easing: "ease-in-out" }
    );
  }, [loginFailed]);
  // Melding alleen wegvegen bij echt typen (autofill geeft geen keydown)
  const clearLoginError = (e) => {
    if (!error) return;
    if (e.key.length === 1 || e.key === "Backspace" || e.key === "Delete") setError("");
  };
  // Code direct controleren zodra hij compleet is
  const [codeStatus, setCodeStatus] = useState("idle");
  const [codeMessage, setCodeMessage] = useState("");
  const [codeShake, setCodeShake] = useState(0);
  const [resetToken, setResetToken] = useState("");
  const [codeExpired, setCodeExpired] = useState(false);
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPassword2, setNewPassword2] = useState("");

  const goTo = (next) => {
    setError("");
    setInfo("");
    setStep(next);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");

    const sanitizedUsername = username.trim();
    const sanitizedPassword = password.trim();

    if (!sanitizedUsername || !sanitizedPassword) {
      setError("Email and password are required");
      return;
    }

    try {
      const response = await apiClient.post("/login", {
        email: sanitizedUsername,
        password: sanitizedPassword,
        remember,
      });

      if (response?.data?.token) {
        // Restanten van een eerdere org-switch (bv. na een verlopen token, waarbij
        // geen logout liep) niet meenemen: anders telt de nieuwe gebruiker als SuperAdmin
        ["impersonating", "real_organization_id", "real_company", "real_role"]
          .forEach(k => localStorage.removeItem(k));
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("organization_id", response.data.organization_id);
        localStorage.setItem("company", response.data.company);
        localStorage.setItem("role", response.data.role);
        localStorage.setItem("org_type", response.data.org_type || "");
        localStorage.setItem("username", response.data.username);
        localStorage.setItem("avatar", response.data.avatar);
        localStorage.setItem("multicompany", response.data.multicompany === 1 ? "true" : "false");
        localStorage.setItem("email", response.data.email);
        localStorage.setItem(POLICY_CHECK_KEY, "1");
        markLogin(remember);
        try {
          if (remember) localStorage.setItem(REMEMBER_EMAIL_KEY, sanitizedUsername);
          else localStorage.removeItem(REMEMBER_EMAIL_KEY);
        } catch { /* geen opslag: niet erg */ }
        setLeaving(true); // navigeren na de fade-out
      } else {
        setError("Invalid response from server");
      }
    } catch (error) {
      console.error("Login error:", error);
      const status = error.response?.status;
      const message =
        error.response?.data?.message ||
        (status === 400 ? "Incorrect email or password." : null) ||
        "Login failed. Please check your credentials.";
      setError(message);
      if (status === 401 || status === 400) {
        setLoginFailed((n) => n + 1);
        setPassword("");
      }
    }
  };

  // Stap 1 van "Forgot password?": code laten mailen
  const requestCode = async (e) => {
    e?.preventDefault();
    if (busy) return;
    setError("");
    setInfo("");
    const email = username.trim();
    if (!email) {
      setError("Please enter your email address");
      return;
    }
    setBusy(true);
    try {
      await apiClient.post("/password-reset/request", { email });
      setCode("");
      setCodeStatus("idle");
      setCodeMessage("");
      setResetToken("");
      setCodeExpired(false);
      setNewPassword("");
      setNewPassword2("");
      setStep("reset");
      setInfo(`If ${email} is known to us, we've sent a 6-digit code. It's valid for 10 minutes.`);
    } catch (err) {
      setError(err.response?.data?.message || "Could not send the code, please try again.");
    } finally {
      setBusy(false);
    }
  };

  // Code controleren; geeft het reset-token terug of null (met melding)
  const verifyCode = async (theCode) => {
    try {
      const { data } = await apiClient.post("/password-reset/verify", {
        email: username.trim(),
        code: theCode,
      });
      return data.reset_token;
    } catch (err) {
      const d = err.response?.data || {};
      const msg = d.message || "Could not check the code, please try again.";
      setCodeStatus("error");
      setCodeMessage(msg);
      setCodeShake((n) => n + 1);
      setCodeExpired(d.reason === "expired");
      setCode("");
      return null;
    }
  };

  // Typen in de vakjes: zodra er 6 cijfers staan meteen controleren
  const onCodeChange = async (next) => {
    setCode(next);
    if (codeStatus === "error" && !codeExpired) setCodeStatus("idle");
    if (!/^\d{6}$/.test(next) || codeExpired) return;
    setCodeStatus("checking");
    const token = await verifyCode(next);
    if (token) {
      setResetToken(token);
      setCodeStatus("ok");
      document.getElementById("new-password")?.focus();
    }
  };

  // Stap 2: code controleren en nieuw wachtwoord zetten
  const resetPassword = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError("");
    if (!resetToken && !/^\d{6}$/.test(code.trim())) {
      setCodeStatus("error");
      setCodeMessage(codeExpired ? codeMessage : "Enter the 6-digit code from the email");
      setCodeShake((n) => n + 1);
      return;
    }
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (newPassword !== newPassword2) {
      setError("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      const token = resetToken || (await verifyCode(code.trim()));
      if (!token) return;
      await apiClient.post("/password-reset/confirm", {
        reset_token: token,
        new_password: newPassword,
      });
      setPassword("");
      setJustReset(true);
      setStep("login");
      setInfo("Your password has been changed. You can now log in.");
    } catch (err) {
      setError(err.response?.data?.message || "Could not reset your password, please try again.");
    } finally {
      setBusy(false);
    }
  };

  const header = {
    login: "Welcome to Cadmus",
    forgot: "Forgot your password?",
    reset: "Choose a new password",
  }[step];

  const subHeader = {
    login: (
      <>
        The all-in-one portal for dental technicians.
        <br />
        Use your email and password to continue.
      </>
    ),
    forgot: "Enter your email address and we'll send you a code to reset your password.",
    reset: "Enter the code from the email and choose a new password.",
  }[step];

  return (
    <div className="login-screen">
      <motion.div
        className="login-container"
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={leaving ? { opacity: 0, y: -16, scale: 0.98 } : { opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: leaving ? 0.25 : 0.4, ease: [0.4, 0, 0.2, 1] }}
        onAnimationComplete={() => { if (leaving) navigate("/", { replace: true }); }}
      >
        <img src={logo}
        alt="Cadmus Logo"
        className="logo"
              draggable={false} // Voorkomt slepen van de afbeelding
                  onContextMenu={(e) => e.preventDefault()} // Blokkeert rechtermuisklik-contextmenu
                  onDragStart={(e) => e.preventDefault()} // Extra blokkade voor drag-events
         />
        <span className="login-header">{header}</span>
        <span className="login-sub-header">{subHeader}</span>
        {error && (
          <div className="error-message login-error" role="alert">
            {error}
          </div>
        )}
        {info && <div className="login-info">{info}</div>}

        {step === "login" && (
          <form onSubmit={handleLogin} className="login-form">
            <Fields className={loginFailed && error ? "is-error" : ""}>
              <input
                type="email"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyDown={clearLoginError}
                placeholder="Email address"
                required
                style={inputStyle}
              />
              <PasswordInput
                key={justReset ? "pw-after-reset" : "pw"}
                blockAutofill={justReset}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={clearLoginError}
                placeholder="Password"
              />
            </Fields>
            <div className="login-options">
              <label className="login-remember">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                <span>Remember me</span>
              </label>
              <button type="button" className="login-link" onClick={() => goTo("forgot")}>
                Forgot password?
              </button>
            </div>
            <ButtonPrimary type="submit">Login to Cadmus</ButtonPrimary>
          </form>
        )}

        {step === "forgot" && (
          <form onSubmit={requestCode} className="login-form">
            <Fields>
              <input
                type="email"
                id="reset-email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Email address"
                required
                autoFocus
                style={inputStyle}
              />
            </Fields>
            <ButtonPrimary type="submit">{busy ? "Sending…" : "Send code"}</ButtonPrimary>
            <button type="button" className="login-link login-back" onClick={() => goTo("login")}>
              Back to login
            </button>
          </form>
        )}

        {step === "reset" && (
          <form onSubmit={resetPassword} className="login-form">
            <div className="login-code-wrap">
              <CodeBoxes value={code} onChange={onCodeChange} status={codeStatus} shake={codeShake} />
              <CodeStatus status={codeStatus} message={codeMessage} />
            </div>
            <Fields>
              <PasswordInput
                id="new-password"
                blockAutofill
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="New password (min. 8 characters)"
                autoComplete="new-password"
              />
              <PasswordInput
                id="new-password-2"
                blockAutofill
                value={newPassword2}
                onChange={(e) => setNewPassword2(e.target.value)}
                placeholder="Repeat new password"
                autoComplete="new-password"
              />
            </Fields>
            <ButtonPrimary type="submit">{busy ? "Saving…" : "Reset password"}</ButtonPrimary>
            <div className="login-options">
              <button type="button" className="login-link" onClick={() => requestCode()} disabled={busy}>
                Send a new code
              </button>
              <button type="button" className="login-link" onClick={() => goTo("login")}>
                Back to login
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
}

export default LoginScreen;
